/**
 * @typedef {Object} StateBase
 * @property {Array} value - The current value of the state
 * @property {Array<Array>} history - Array of previous values for undo functionality
 * @property {Array<Array>} future - Array of future values for redo functionality
 * @property {Set<function(Array): void>} listeners - Set of subscriber callback functions
 * @property {boolean} canUndo - Whether undo operation is possible
 * @property {boolean} canRedo - Whether redo operation is possible
 * @property {function(function(Array): void): function(): void} subscribe - Subscribe to state changes
 * @property {function(): Promise<Array>} notifyListeners - Notify all listeners of state changes
 * @property {function(): Promise<Array>} clear - Reset state to initial value
 * @property {function(): Promise<Array>} undo - Undo last state change
 * @property {function(): Promise<Array>} redo - Redo last undone change
 */

import { composerCreated, tagsLoaded } from '@signals/viewer';
import _State from '@states/_State';
import { custom_config } from '@utils/Config.js';
import { blockPinchZoom } from '@utils/scrollstop.js';
import { qualitySetting } from '@utils/viewer/mobileSettings.js';
import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';

/**
 * @typedef {StateBase & {
 *   activeModel: Array,
 *   setActiveModel: function(Array): Promise<Array>,
 *   setActiveModelByIndex: function(Array): Promise<Array>,
 *   getActiveModelIndex: function(): number
 * }} ActiveModelStateType
 */

/** @type {ActiveModelStateType} */
export const ActiveModelState = _State('activeModel', 'setActiveModel', []);

/**
 * Get the index of the active model in the available models list.
 * Takes the first active model (index 0) and finds its index in AvailableModelState.
 * @returns {number} Index of the active model in available models, or -1 if no active model or not found
 */
ActiveModelState.getActiveModelIndex = function () {
  const activeModels = this.activeModel;
  const availableModels = AvailableModelState.availableModel;

  // Return -1 if no active models
  if (!Array.isArray(activeModels) || activeModels.length === 0) {
    return -1;
  }

  // Get the first active model
  const firstActiveModel = activeModels[0];

  // Find its index in the available models list
  const index = availableModels.findIndex(
    model =>
      model === firstActiveModel ||
      model.uuid === firstActiveModel?.uuid ||
      model.cadFileModelId === firstActiveModel?.cadFileModelId ||
      model.referenceID === firstActiveModel?.referenceID,
  );

  return index !== -1 ? index : -1;
};

/**
 * Set the active model by its index in the available models list.
 * @param {number} index - Index of the model in AvailableModelState to set as active
 * @returns {Promise<Array>}
 */
ActiveModelState.setActiveModelByIndex = function (index) {
  const availableModels = AvailableModelState.availableModel;

  if (index < 0 || index >= availableModels.length) {
    return this.setActiveModel([]);
  }

  return this.setActiveModel([availableModels[index]]);
};

/**
 * @typedef {StateBase & {
 *   availableModel: Array,
 *   setAvailableModel: function(Array): Promise<Array>
 * }} AvailableModelStateType
 */

/** @type {AvailableModelStateType} */
export const AvailableModelState = _State(
  'availableModel',
  'setAvailableModel',
  [1],
);

/**
 * @typedef {StateBase & {
 *   loadedModel: Array,
 *   setLoadedModel: function(Array): Promise<Array>
 * }} LoadedModelStateType
 */
/** @type {LoadedModelStateType} */
export const LoadedModelState = _State('loadedModel', 'setLoadedModel', {});

/**
 * @typedef {StateBase & {
 *   measurements: Array,
 *   setMeasurements: function(Array): Promise<Array>
 * }} MeasurementsStateType
 */
/** @type {MeasurementsStateType} */
export const MeasurementsState = _State('measurements', 'setMeasurements', []);

/**
 * @typedef {StateBase & {
 *   comments: Array,
 *   setComments: function(Array): Promise<Array>
 * }} CommentsStateType
 */
/** @type {CommentsStateType} */
export const CommentsState = _State('comments', 'setComments', []);

/**
 * @typedef {Object} MediaListType
 * @property {Array<any>} pdf
 * @property {Array<any>} video
 * @property {Array<any>} image
 * @property {Array<any>} other
 * @property {Object} firstItem
 */
/**
 * @typedef {StateBase & {
 *   mediaList: MediaListType,
 *   setMediaList: function(MediaListState): Promise<Array>
 * }} MediaListStateType
 */
/** @type {MediaListStateType} */
export const MediaListState = _State('mediaList', 'setMediaList', {
  pdf: {},
  video: {},
  image: {},
  other: {},
  firstItem: {},
});

/**
 * @typedef {StateBase & {
 *   tag: Object,
 *   setTag: function(Array): Promise<Array>
 * }} SelectedTagType
 */
/** @type {SelectedTagType} */
export const SelectedTagState = _State('tag', 'setTag', {});

/**
 * @typedef {StateBase & {
 *   members: Array,
 *   setMembers: function(Array): Promise<Array>
 * }} MemberStateType
 */
/** @type {MemberStateType} */
export const MemberState = _State('members', 'setMembers', []);

/**
 * @typedef {StateBase & {
 *   scene: THREE.Scene,
 *   setScene: function(THREE.Scene): Promise<Array>
 * }} SceneStateType
 */
/** @type {SceneStateType} */
export const SceneState = _State('scene', 'setScene', new THREE.Scene());
SceneState.scene.name = 'Main';
const axesHelper = new THREE.AxesHelper(5);
axesHelper.setColors('red', 'green', 'blue'); // x,y,z
axesHelper.visible = false;
SceneState.scene.add(axesHelper);

/**
 * @typedef {StateBase & {
 *   sceneHelper: THREE.Scene,
 *   setSceneHelper: function(THREE.Scene): Promise<Array>
 * }} SceneHelperStateType
 */
/** @type {SceneHelperStateType} */
export const SceneHelperState = _State(
  'sceneHelper',
  'setSceneHelper',
  new THREE.Scene(),
);

/**
 * @typedef {StateBase & {
 *   renderer: THREE.WebGLRenderer|null,
 *   setRenderer: function(THREE.WebGLRenderer|null): Promise<Array>,
 * }} RendererStateType
 */
const createRenderer = (config = {}) => {
  const isMobile =
    typeof isMobileLowMemory === 'function' ? isMobileLowMemory() : false;
  const currentQualitySetting = qualitySetting;
  blockPinchZoom();
  const rendererConfig = {
    antialias: isMobile
      ? false
      : config.antialias !== undefined
        ? config.antialias
        : custom_config?.getKey?.('project/renderer/antialias'),
    powerPreference: isMobile ? 'low-power' : 'high-performance',
    precision: isMobile ? 'mediump' : 'highp',
    logarithmicDepthBuffer: false,
    preserveDrawingBuffer: false,
    alpha: false,
    stencil: false,
    depth: true,
    premultipliedAlpha: false,
  };
  const renderer = new THREE.WebGLRenderer(rendererConfig);
  renderer.physicallyCorrectLights =
    config.physicallyCorrectLights !== undefined
      ? config.physicallyCorrectLights
      : custom_config?.getKey?.('project/renderer/physicallyCorrectLights');
  const shadowsEnabled = isMobile
    ? false
    : config.shadows !== undefined
      ? config.shadows
      : custom_config?.getKey?.('project/renderer/shadows');
  renderer.shadowMap.enabled = shadowsEnabled;
  renderer.shadowMap.type =
    config.shadowType !== undefined
      ? config.shadowType
      : custom_config?.getKey?.('project/renderer/shadowType');
  renderer.toneMapping =
    config.toneMapping !== undefined
      ? config.toneMapping
      : custom_config?.getKey?.('project/renderer/toneMapping');
  renderer.toneMappingExposure =
    config.toneMappingExposure !== undefined
      ? config.toneMappingExposure
      : custom_config?.getKey?.('project/renderer/toneMappingExposure');
  if (isMobile) {
    const pixelRatio =
      Math.min(window.devicePixelRatio, 2) / currentQualitySetting;
    renderer.setPixelRatio(pixelRatio);
  }

  const composer = createComposer(renderer);
  renderer.composer = composer;
  composerCreated?.dispatch?.(composer);

  return renderer;
};
const createComposer = renderer => {
  const composer = new EffectComposer(renderer);
  composer.setSize(window.innerWidth, window.innerHeight);
  composer.setPixelRatio(window.devicePixelRatio / qualitySetting);
  return composer;
};

/** @type {RendererStateType} */
export const RendererState = _State(
  'renderer',
  'setRenderer',
  createRenderer(),
);

/**
 * @typedef {StateBase & {
 *   composer: EffectComposer|null,
 *   setComposer: function(EffectComposer|null): Promise<Array>,
 * }} ComposerStateType
 */
/** @type {ComposerStateType} */
export const ComposerState = _State(
  'composer',
  'setComposer',
  createComposer(RendererState.renderer),
);

/**
 * @typedef {StateBase & {
 *   firstChunkPromise: Array<any>,
 *   push: function(any): Promise<Array>
 * }} FirstChunkPromiseType
 */
/** @type {FirstChunkPromiseType} */
export const FirstChunkPromise = _State(
  'firstChunkPromise',
  'setFirstChunkPromise',
  [],
);
FirstChunkPromise.push = async function (entry) {
  const arr = Array.isArray(this.firstChunkPromise)
    ? [...this.firstChunkPromise, entry]
    : [entry];
  return this.setFirstChunkPromise(arr);
};

/**
 * @typedef {StateBase & {
 *   background: string,
 *   setBackground: function(string): Promise<Array>
 * }} BackgroundStateType
 */
/** @type {BackgroundStateType} */
export const BackgroundState = _State('background', 'setBackground', '');

/**
 * @typedef {StateBase & {
 *    backgroundCnfg: string,
 *    setbackgroundCnfg: function(string): Promise<Array>
 * }} BackgroundCnfgStateType
 */
/** @type {BackgroundCnfgStateType} */
export const BackgroundCnfgState = _State(
  'backgroundCnfg',
  'setbackgroundCnfg',
  { mode: 'Base', texure: '' },
);

/**
 * @typedef {StateBase & {
 *   cadfile: Object,
 *   setCadfile: function(Object): Promise<Array>
 * }} CadFileStateType
 */
/** @type {CadFileStateType} */
export const CadFileState = _State('cadfile', 'setCadfile', {});

/**
 * @typedef {StateBase & {
 *   access: Object,
 *   setAccess: function(Object): Promise<Array>
 * }} CadFileAccessStateType
 */
/** @type {CadFileAccessStateType} */
export const CadFileAccessState = _State('access', 'setAccess', {});

/**
 * @typedef {StateBase & {
 *   isAutoDetectionAllowed: boolean,
 *   setIsAutoDetectionAllowed: function(boolean): Promise<Array>
 * }} IsAutoDetectionAllowedStateType
 */
/** @type {IsAutoDetectionAllowedStateType} */
export const IsAutoDetectionAllowedState = _State(
  'isAutoDetectionAllowed',
  'setIsAutoDetectionAllowed',
  false,
);

/**
 * @typedef {StateBase & {
 *   quotationTypes: Array,
 *   setQuotationTypes: function(Array): Promise<Array>
 * }} QuotationTypesStateType
 */
/** @type {QuotationTypesStateType} */
export const QuotationTypesState = _State(
  'quotationTypes',
  'setQuotationTypes',
  [],
);

/**
 * @typedef {StateBase & {
 *   ownerQuotation: object,
 *   setOwnerQuotation: function(object): Promise<Array>
 * }} OwnerQuotationStateType
 */
/** @type {OwnerQuotationStateType} */
export const OwnerQuotationState = _State(
  'ownerQuotation',
  'setOwnerQuotation',
  {},
);

/**
 * @typedef {StateBase & {
 *   currentActiveCanvas: string,
 *   setCurrentActiveCanvas: function(string): Promise<Array>
 * }} CurrentActiveCanvasStateType
 */
/** @type {CurrentActiveCanvasStateType} */
export const CurrentActiveCanvasState = _State(
  'currentActiveCanvas',
  'setCurrentActiveCanvas',
  null,
);

/**
 * @typedef {StateBase & {
 *   mainObjSize: number,
 *   setMainObjSize: function(number): Promise<Array>
 * }} MainObjSizeStateType
 */
/** @type {MainObjSizeStateType} */
export const MainObjSizeState = _State('mainObjSize', 'setMainObjSize', 1);

/**
 * @typedef {StateBase & {
 *   zoomValue: number,
 *   setZoomValue: function(number): Promise<Array>
 * }} ZoomValueStateType
 */
/** @type {ZoomValueStateType} */
export const ZoomValueState = _State('zoomValue', 'setZoomValue', 25);

/**
 * @typedef {StateBase & {
 *   axisSelected: string,
 *   setAxisSelected: function(string): Promise<Array>
 * }} AxisSelectedStateType
 */
/** @type {AxisSelectedStateType} */
export const AxisSelectedState = _State('axisSelected', 'setAxisSelected', 'y');

/**
 * @typedef {StateBase & {
 *   newTagIntersect: object,
 *   setNewTagIntersect: function(object): Promise<Array>
 * }} NewTagIntersectStateType
 */
/** @type {NewTagIntersectStateType} */
export const NewTagIntersectState = _State(
  'newTagIntersect',
  'setNewTagIntersect',
  {},
);

/**
 * @typedef {StateBase & {
 *   tagColor: number,
 *   setTagColor: function(number): Promise<Array>
 * }} TagColorStateType
 */
/** @type {TagColorStateType} */
export const TagColorState = _State('tagColor', 'setTagColor', 0);

/**
 * @typedef {StateBase & {
 *   selectedComment: object,
 *   setSelectedComment: function(object): Promise<Array>
 * }} SelectedCommentStateType
 */
/** @type {SelectedCommentStateType} */
export const SelectedCommentState = _State(
  'selectedComment',
  'setSelectedComment',
  null,
);

/**
 * @typedef {StateBase & {
 *   tags: Array,
 *   setTags: function(Array): Promise<Array>
 * }} TagsStateType
 */
/** @type {TagsStateType} */
export const TagsState = _State('tags', 'setTags', []);
TagsState.subscribe(() => {
  tagsLoaded.dispatch();
});

/**
 * @typedef {StateBase & {
 *   measurementsScaleRemoved: Array,
 *   setMeasurementsScaleRemoved: function(Array): Promise<Array>
 * }} MeasurementsScaleRemovedStateType
 */
/** @type {MeasurementsScaleRemovedStateType} */
export const MeasurementsScaleRemovedState = _State(
  'measurementsScaleRemoved',
  'setMeasurementsScaleRemoved',
  [],
);

/**
 * @typedef {StateBase & {
 *   hoveredSelectionPoint: object,
 *   setHoveredSelectionPoint: function(object): Promise<Array>
 * }} HoveredSelectionPointStateType
 */
/** @type {HoveredSelectionPointStateType} */
export const HoveredSelectionPointState = _State(
  'hoveredSelectionPoint',
  'setHoveredSelectionPoint',
  null,
);

/**
 * @typedef {StateBase & {
 *   mode: string,
 *   setMode: function(string): Promise<Array>
 * }} ModeStateType
 */
/** @type {ModeStateType} */
export const ModeState = _State('mode', 'setMode', 'cursor');

/**
 * @typedef {StateBase & {
 *   selected: object,
 *   setSelected: function(object): Promise<Array>
 * }} SelectedState
 */
/** @type {SelectedState} */
export const SelectedState = _State('selected', 'setSelected', null);

/**
 * @typedef {StateBase & {
 *   forceBackground: boolean,
 *   setForceBackground: function(boolean): Promise<Array>
 * }} ForceBackgroundStateType
 */
/** @type {ForceBackgroundStateType} */
export const ForceBackgroundState = _State(
  'forceBackground',
  'setForceBackground',
  false,
);

/**
 * @typedef {StateBase & {
 *   viewportSize: number,
 *   setViewportSize: function(number): Promise<Array>
 * }} ViewportSizeStateType
 */
/** @type {ViewportSizeStateType} */
export const ViewportSizeState = _State('viewportSize', 'setViewportSize', -1);

/**
 * @typedef {StateBase & {
 *   alertQueue: Array,
 *   setAlertQueue: function(Array): Promise<Array>
 * }} AlertQueueStateType
 */
/** @type {AlertQueueStateType} */
export const AlertQueueState = _State('alertQueue', 'setAlertQueue', ['init']);

/**
 * @typedef {StateBase & {
 *   openedHeader: object,
 *   setOpenedHeader: function(object): Promise<Array>
 * }} OpenedHeaderStateType
 */
/** @type {OpenedHeaderStateType} */
export const OpenedHeaderState = _State(
  'openedHeader',
  'setOpenedHeader',
  null,
);

/**
 * @typedef {StateBase & {
 *   usingMediaViewer: boolean,
 *   setUsingMediaViewer: function(boolean): Promise<Array>
 * }} UsingMediaViewerStateType
 */
/** @type {UsingMediaViewerStateType} */
export const UsingMediaViewerState = _State(
  'usingMediaViewer',
  'setUsingMediaViewer',
  false,
);

/**
 * @typedef {StateBase & {
 *   isShared: boolean,
 *   setIsShared: function(boolean): Promise<Array>
 * }} IsSharedStateType
 */
/** @type {IsSharedStateType} */
export const IsSharedState = _State('isShared', 'setIsShared', false);

/**
 * @typedef {StateBase & {
 *   panorama: { isPanorama: boolean, panoramaAttachmentId: string, panoramaKey: string },
 *   setPanorama: function({ isPanorama: boolean, panoramaAttachmentId: string, panoramaKey: string }): Promise<Array>
 * }} PanoramaStateType
 */
/** @type {PanoramaStateType} */
export const PanoramaState = _State('panorama', 'setPanorama', {
  isPanorama: false,
  panoramaAttachmentId: '',
  panoramaKey: '',
});

/**
 * @typedef {StateBase & {
 *   minimalViewer: boolean,
 *   setMinimalViewer: function(boolean): Promise<Array>
 * }} MinimalViewerStateType
 */
/** @type {MinimalViewerStateType} */
export const MinimalViewerState = _State(
  'minimalViewer',
  'setMinimalViewer',
  false,
);

/**
 * @typedef {StateBase & {
 *   showTags: boolean,
 *   setShowTags: function(boolean): Promise<Array>
 * }} ShowTagsStateType
 */
/** @type {ShowTagsStateType} */
export const ShowTagsState = _State('showTags', 'setShowTags', true);

/**
 * @typedef {StateBase & {
 *   measurementsScale: Array,
 *   setMeasurementsScale: function(Array): Promise<Array>
 * }} MeasurementsScaleStateType
 */
/** @type {MeasurementsScaleStateType} */
export const MeasurementsScaleState = _State(
  'measurementsScale',
  'setMeasurementsScale',
  [],
);

/**
 * @typedef {StateBase & {
 *   selections: Array,
 *   setSelections: function(Array): Promise<Array>
 * }} SelectionsStateType
 */
/** @type {SelectionsStateType} */
export const SelectionsState = _State('selections', 'setSelections', []);

/**
 * @typedef {StateBase & {
 *   modelOrder: Array<string|number>,
 *   setModelOrder: function(Array<string|number>): Promise<Array>
 * }} ModelOrderStateType
 */
/** @type {ModelOrderStateType} */
export const ModelOrderState = _State('modelOrder', 'setModelOrder', []);

/**
 * @typedef {StateBase & {
 *   grids: Object,
 *   setGrids: function(Array<string|number>): Promise<Array>,
 *   setGrid: function(string, any): Promise<Array>,
 *   getGrid: function(string): Object|null
 * }} GridStateType
 */
/** @type {GridStateType} */
export const GridState = _State('grids', 'setGrids', {});
GridState.getGrid = gridId => {
  return GridState.value[gridId] || null;
};
GridState.setGrid = (gridId, grid) => {
  const grids = { ...GridState.value, [gridId]: grid };
  return GridState.setGrids(grids);
};

/**
 * @typedef {StateBase & {
 *   transformControls: Object,
 *   setTransformControls: function(Object): Promise<Array>
 * }} TransformControlsStateType
 */
/** @type {TransformControlsStateType} */
export const TransformControlsState = _State(
  'transformControls',
  'setTransformControls',
  {},
);

/**
 * @typedef {StateBase & {
 *   parentCommentID: string|null,
 *   setParentCommentID: function(string|null): Promise<Array>
 * }} ParentCommentIDStateType
 */
/** @type {ParentCommentIDStateType} */
export const ParentCommentIDState = _State(
  'parentCommentID',
  'setParentCommentID',
  null,
);

/**
 * @typedef {StateBase & {
 *   tagList2d: Object,
 *   setTagList2d: function(Object): Promise<Object>,
 *   addTag: function(any): Promise<Object>,
 *   removeTag: function(string): Promise<Object>
 * }} TagList2dStateType
 */
/** @type {TagList2dStateType} */
export const TagList2dState = _State('tagList2d', 'setTagList2d', {});

TagList2dState.addTag = tag => {
  const currentTags = TagList2dState.value;
  if (!currentTags[tag.id]) {
    const newTags = { ...currentTags, [tag.id]: tag };
    return TagList2dState.setTagList2d(newTags);
  }
  return Promise.resolve(currentTags);
};

TagList2dState.removeTag = tagId => {
  const currentTags = TagList2dState.value;
  if (currentTags[tagId]) {
    const newTags = { ...currentTags };
    delete newTags[tagId];
    return TagList2dState.setTagList2d(newTags);
  }
  return Promise.resolve(currentTags);
};

/**
 * @typedef {StateBase & {
 *   forceComposer: boolean,
 *   setForceComposer: function(boolean): Promise<Array>
 * }} ForceComposerStateType
 */
/** @type {ForceComposerStateType} */
export const ForceComposerState = _State(
  'forceComposer',
  'setForceComposer',
  false,
);

/**
 * @typedef {StateBase & {
 *   activeDrawingTool: string|null,
 *   setActiveDrawingTool: function(string|null): Promise<Array>
 * }} ActiveDrawingToolStateType
 */
/** @type {ActiveDrawingToolStateType} */
export const ActiveDrawingToolState = _State(
  'activeDrawingTool',
  'setActiveDrawingTool',
  null,
);

/**
 * @typedef {StateBase & {
 *   screenOrientation: string,
 *   setScreenOrientation: function(string): Promise<Array>
 * }} ScreenOrientationStateType
 */
/** @type {ScreenOrientationStateType} */
export const ScrreenOrientationState = _State(
  'screenOrientation',
  'setScreenOrientation',
  'portrait',
);

/**
 * @typedef {StateBase & {
 *   lightList: Object,
 *   setLightList: function(Object): Promise<Array>
 * }} LightListStateType
 */
/** @type {LightListStateType} */
export const LightListState = _State('lightList', 'setLightList', {});

/**
 * @typedef {StateBase & {
 *   collapseReplies: Array<function(): void>,
 *   setCollapseReplies: function(Array<function(): void>): Promise<Array>
 * }} CollapseRepliesStateType
 */
/** @type {CollapseRepliesStateType} */
export const CollapseRepliesState = _State(
  'collapseReplies',
  'setCollapseReplies',
  [],
);

/**
 * @typedef {StateBase & {
 *   zoomDisabled: boolean,
 *   setZoomDisabled: function(boolean): Promise<Array>
 * }} ZoomDisabledStateType
 */
/** @type {ZoomDisabledStateType} */
export const ZoomDisabledState = _State(
  'zoomDisabled',
  'setZoomDisabled',
  false,
);

/**
 * @typedef {StateBase & {
 *   modelScale: number,
 *   setModelScale: function(number): Promise<Array>
 * }} ModelScaleStateType
 */
/** @type {ModelScaleStateType} */
export const ModelScaleState = _State('modelScale', 'setModelScale', 50);
